import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const DigitalMarketing = () => {
  const phrases = [
    "Digital Marketing",
    "Brand Growth  "
  ];

  const cardData = [
    { type: 'purple-clay', content: '📈' },
    { type: 'smooth-wave', content: '🎯' },
    { type: 'abstract-blocks', content: '📣' },
    { type: 'pink-sphere', content: '🚀' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const designServices = [
    { icon: "🔍", title: "Search Engine Optimization (SEO)", desc: "Elevate keyword rankings and boost organic visibility to drive high-intent visitors directly to your website." },
    { icon: "📱", title: "Social Media Marketing (SMM)", desc: "Build thriving online communities and run high-engagement campaigns across Facebook, Instagram, and LinkedIn." },
    { icon: "🎯", title: "Pay-Per-Click Ads (PPC)", desc: "Generate immediate conversions with targeted search and display campaigns on Google Ads and social channels." },
    { icon: "✍️", title: "Content Strategy & Creation", desc: "Position your brand as a market leader using engaging blog copy, graphics, and value-focused newsletters." },
    { icon: "✉️", title: "Email Marketing Automation", desc: "Nurture generated leads and build customer lifetime value with personalized, structured drip campaigns." },
    { icon: "🔄", title: "Conversion Optimization (CRO)", desc: "Fine-tune customer journeys and conversion setups to lower your acquisition costs and maximize returns." },
    { icon: "👑", title: "Brand Identity & Positioning", desc: "Establish continuous brand trust with strategic corporate narratives, professional visuals, and unified styling." }
  ];

  const devServices = [
    { icon: "📊", title: "Comprehensive Data Analytics", desc: "Track conversion funnels and platform metrics transparently to make informed marketing investments." },
    { icon: "🛡️", title: "Reputation Management (ORM)", desc: "Monitor brand mentions and scale positive consumer trust across prominent public evaluation spaces." },
    { icon: "📍", title: "Local Search Optimization", desc: "Dominate location-based queries and maximize foot traffic by tuning local business map profiles." },
    { icon: "🚀", title: "Growth Hacking Strategies", desc: "Deploy fast, testing-oriented audience acquisition paths built to identify scalable marketing wins." },
    { icon: "🤝", title: "Affiliate & Influencer Strategy", desc: "Form partnerships with key niche creators and custom publishers to build authentic brand awareness." },
    { icon: "💬", title: "Marketing Automation Flow", desc: "Integrate chat funnels and CRM workflows to quickly interact with incoming customer requests." },
    { icon: "📈", title: "Competitor Market Analysis", desc: "Audit rival digital tactics, backlink profiles, and ad copies to capture strategic growth windows." },
    { icon: "🎨", title: "Display Advertising Designs", desc: "Draft captivating graphic assets optimized to capture consumer attention across publisher networks." },
    { icon: "🛠️", title: "Consulting & Custom Roadmaps", desc: "Align corporate growth goals with tailored multi-channel frameworks designed for predictable scale." }
  ];

  const technologies = {
    "Analytics & Tracking": ["Google Analytics 4", "Google Tag Manager", "Hotjar", "Mixpanel"],
    "Paid Advertising": ["Google Ads Manager", "Meta Ads Manager", "LinkedIn Campaign Manager"],
    "SEO & Research Tools": ["SEMrush", "Ahrefs", "Google Search Console", "Screaming Frog"],
    "Automation & CRM": ["HubSpot", "Mailchimp", "Klaviyo", "Zapier", "Salesforce"],
    "Design & Creation": ["Canva Pro", "Adobe Suite", "Figma", "Buffer", "Hootsuite"]
  };

  const processSteps = [
    { num: "01", title: "Audit & Analysis", desc: "Evaluating current performance channels, market dynamics, and rival campaign ecosystems." },
    { num: "02", title: "Strategic Blueprinting", desc: "Defining target audience avatars, core KPIs, budget allocation, and channel priorities." },
    { num: "03", title: "Asset Preparation", desc: "Building optimized landing configurations, ad creative variations, and messaging hooks." },
    { num: "04", title: "Campaign Launch", desc: "Deploying targeted setups across search frameworks and selected social placement layers." },
    { num: "05", title: "Performance Tuning", desc: "A/B testing ad variations, monitoring cost metrics, and refining target parameters." },
    { num: "06", title: "Conversion Scaling", desc: "Funneling spend into top-performing channel patterns to build maximum business pipeline value." },
    { num: "07", title: "Transparent Insights", desc: "Delivering detailed multi-channel ROI breakdowns, trend reviews, and next-stage plans." }
  ];

  const industries = [
    "E-Commerce & Retail", "B2B SaaS & Tech", "Healthcare & Medical Services", "Real Estate & Housing",
    "Financial & Banking Tools", "Educational Institutes", "Professional Services", "Travel & Hospitality",
    "Logistics & Transport", "Entertainment Networks", "Emerging Startups", "Corporate Enterprises"
  ];

  const designBenefits = [
    "Data-Driven Architecture", "Hyper-Targeted Placements", "Consistent Multi-Channel Touch",
    "Rapid Conversion Uplift", "Transparent Performance Data", "Enhanced Brand Equity",
    "Lower Customer Acquisition Costs", "Predictable Pipeline Scale"
  ];

  const whyChooseUs = [
    "Certified Performance Marketers", "Custom Growth Frameworks", "ROI-Focused Strategies",
    "Advanced SEO Implementations", "Data-Backed Optimization Engine", "Transparent Analytics Dashboards",
    "Creative Multi-Format Content", "Dedicated Account Orchestration", "Rapid Channel Adaptation",
    "Continuous Funnel Improvements", "Cutting-Edge Marketing Stack", "100% Scalable Results Commitment"
  ];



  return (

    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Digital Marketing Services | SEO, PPC & Social Media Marketing | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Digital Marketing Services | SEO, PPC & Social Media Marketing | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers result-driven digital marketing services, including SEO, PPC advertising, social media marketing, content marketing, email marketing, online reputation management, Google Ads, and lead generation solutions to grow your business online."
        />

        <meta
          name="keywords"
          content="Digital Marketing, Digital Marketing Services, SEO Services, Search Engine Optimization, PPC Advertising, Google Ads, Social Media Marketing, Facebook Marketing, Instagram Marketing, LinkedIn Marketing, Content Marketing, Email Marketing, Online Reputation Management, Lead Generation, Local SEO, Technical SEO, E-commerce Marketing, Performance Marketing, Digital Marketing Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/digital-marketing"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Digital Marketing Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Boost your online presence with Affsoft IT Solutions Pvt. Ltd. We provide expert SEO, PPC, social media marketing, content marketing, Google Ads, and performance marketing services to increase traffic, leads, and conversions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/digital-marketing"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Digital Marketing Services | SEO, PPC & Social Media Marketing | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Drive business growth with expert digital marketing services from Affsoft IT Solutions Pvt. Ltd. We specialize in SEO, PPC, social media marketing, content marketing, and lead generation strategies."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="digital-marketing-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0915' // dark fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Digital Marketing &nbsp; and &nbsp;</span>
              <span>Brand Growth </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-warning fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Digital Marketing Solutions</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we execute growth-centric, ROI-driven marketing campaigns engineered to expand your business footprint. Our digital marketing frameworks combine data analytics, compelling content, and platform engineering to outpace your competition and turn search traffic into loyal customers.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-warning fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= STRATEGY & ORGANIC SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Organic & Paid Growth</span>
              <h2 className="fw-bold text-dark display-6">Strategic Solutions to Build Market Authority</h2>
              <p className="text-muted mt-3">
                Your online reach dictates your business growth path. At Affsoft IT Solutions Pvt. Ltd., we deliver multi-channel campaigns that align perfectly with user behaviors, ensuring your message lands exactly where your prospects gather.
              </p>
            </div>

            <div className="row g-4">
              {designServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Marketing Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Invest in Structured Digital Marketing?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= OPTIMIZATION SECTION ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Performance Operations</span>
              <h2 className="fw-bold text-dark display-6">Advanced Campaigns Formulated for Revenue</h2>
              <p className="text-muted mt-3">
                We leverage reliable automation platforms and data streams to ensure optimal resource deployment. From complex optimization funnels to targeted audience remarketing, our execution maps deliver traceable brand visibility and customer conversions.
              </p>
            </div>

            <div className="row g-4">
              {devServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">MarTech Stack</span>
              <h2 className="fw-bold display-6">Marketing Tools We Leverage</h2>
              <p className="text-light-50">We analyze and execute complex ad configurations using industry-leading platform engines.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Execution Flow</span>
              <h2 className="fw-bold text-dark display-6">Our Data-Backed Growth Methodology</h2>
              <p className="text-muted">A clear, agile step-by-step roadmap driving continuous campaign improvement.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Scale</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We provide performance-oriented marketing blueprints built to scale operations predictably.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default DigitalMarketing;